import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DataGrid from '@afosto/app-components-react/DataGrid';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Hidden from '@material-ui/core/Hidden';
import { calculations as calculationsApi } from '@afosto/order/api/Calculations';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { orderFormatter } from './formatters';
import translations from './translations';
import DataList from '../DataList';
import OrderListItem from '../OrderListItem';

const fetchOrder = async orderId => {
  try {
    const orderResponse = await ordersApi.getOrder(orderId);
    const { data: order } = orderResponse;

    if (order?.calculations?.length > 0) {
      const [lastCalculation] = order.calculations.reverse();

      if (lastCalculation) {
        const calculationResponse = await calculationsApi
          .getCalculation(lastCalculation?.id)
          .catch(() => {
            // Catch only
          });
        const { data: calculation } = calculationResponse ?? {};

        return { ...order, total: calculation?.total };
      }
    }

    return order;
  } catch (err) {
    return null;
  }
};

const fetchOrders = orderIds => async () => {
  try {
    const promises = orderIds.map(orderId => fetchOrder(orderId));
    const responses = await Promise.allSettled(promises).then(result =>
      result.filter(promise => promise.status === 'fulfilled'),
    );

    return responses.reduce((acc, response) => {
      if (response?.value) {
        return [...acc, response.value];
      }

      return acc;
    }, []);
  } catch (err) {
    return [];
  }
};

const OrdersSection = ({ orderIds }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const dataSource = useDataGrid(fetchOrders(orderIds), {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.orders.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'order',
        },
        {
          key: 'total',
          name: intl.formatMessage({
            id: 'dataGrid.columns.orders.amount',
            defaultMessage: 'Amount',
            description: 'The column name for column amount',
          }),
          type: 'currency',
        },
        {
          key: 'type',
          name: intl.formatMessage({
            id: 'dataGrid.columns.orders.type',
            defaultMessage: 'Type',
            description: 'The column name for column type',
          }),
          type: 'translation',
          formatOptions: {
            translationKey: 'orderTypeOptions.{value}',
          },
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.orders.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'date',
        },
      ],
    },
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    dataSourceRef.current.reloadDataSource();
  }, [orderIds]);

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      {hasPermissions ? (
        <>
          <Hidden implementation="js" mdUp>
            <DataList
              dataSource={dataSource}
              renderItem={order => <OrderListItem order={order} key={order?.id} />}
            />
          </Hidden>
          <Hidden implementation="js" smDown>
            <DataGrid dataSource={dataSource} formatters={{ order: orderFormatter }} />
          </Hidden>
        </>
      ) : (
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      )}
    </Card>
  );
};

OrdersSection.propTypes = {
  orderIds: PropTypes.array,
};

OrdersSection.defaultProps = {
  orderIds: [],
};

export default OrdersSection;
